import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { fmtDate, fmtHours, money } from "@/components/admin/format";

const Tile = ({ label, value, sub, testid }) => (
  <div className="border border-[var(--o-border)] p-5" data-testid={testid}>
    <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--o-muted3)]">{label}</div>
    <div className="font-display text-3xl font-black mt-2 tabular-nums">{value}</div>
    {sub && <div className="text-xs text-[var(--o-muted2)] mt-1">{sub}</div>}
  </div>
);

export const OverviewPanel = () => {
  const [o, setO] = useState(null);
  const [people, setPeople] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/admin/overview"), api.get("/admin/users")])
      .then(([a, b]) => { setO(a.data); setPeople(b.data); })
      .catch((e) => toast.error(errMsg(e)));
  }, []);
  if (!o) return <div className="h-40 shimmer" />;
  const top = [...people].sort((a, b) => b.usage.cost - a.usage.cost).slice(0, 8);
  return (
    <div className="space-y-6" data-testid="overview-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile label="People" value={o.users.total} sub={`${o.users.active} active · ${o.users.invited} invited · ${o.users.admins} admin${o.users.admins === 1 ? "" : "s"}`} testid="tile-people" />
        <Tile label="Meetings · 30 days" value={o.last_30_days.meetings} sub={`${o.all_time.meetings} saved in all`} testid="tile-meetings" />
        <Tile label="Meeting time · 30 days" value={`${o.last_30_days.hours} h`} sub={`${o.all_time.hours} h in all`} testid="tile-hours" />
        <Tile label="AI cost · 30 days" value={money(o.last_30_days.cost)} sub={`${money(o.all_time.cost)} in all, estimated`} testid="tile-cost" />
      </div>
      <div className="border border-[var(--o-border)]">
        <div className="px-5 py-4 border-b border-[var(--o-border)]">
          <h2 className="font-display text-base md:text-lg font-bold">Running now</h2>
        </div>
        {o.live_now.length === 0 && <p className="px-5 py-4 text-sm text-[var(--o-muted3)]">No meetings are running.</p>}
        {o.live_now.map((m) => (
          <div key={m.id} className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[var(--o-border-soft)] last:border-b-0 text-sm" data-testid="live-row">
            <span className="flex items-center gap-2 min-w-0 truncate">
              <span className="h-2 w-2 rounded-full bg-[#FF3B30] shrink-0" />
              {m.private && <Lock size={12} className="text-[var(--o-warn)] shrink-0" />} <span className="truncate">{m.title}</span>
            </span>
            <span className="text-xs text-[var(--o-muted2)] shrink-0">{m.owner || "—"}</span>
            {!m.private && <Link to={`/session/${m.id}`} className="font-mono text-xs text-[#10B981] hover:underline shrink-0">open</Link>}
          </div>
        ))}
      </div>
      <div className="border border-[var(--o-border)]">
        <div className="px-5 py-4 border-b border-[var(--o-border)]">
          <h2 className="font-display text-base md:text-lg font-bold">Usage by person</h2>
          <p className="text-xs text-[var(--o-muted2)] mt-1">Saved meetings, all time. Private meetings aren't saved, so they only count while running.</p>
        </div>
        {top.map((u) => (
          <div key={u.id} className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_90px_90px_90px_130px] gap-3 px-5 py-3 border-b border-[var(--o-border-soft)] last:border-b-0 text-sm" data-testid="usage-row">
            <span className="truncate">{u.name}</span>
            <span className="font-mono text-xs text-[var(--o-muted)] text-right">{u.usage.meetings} mtg</span>
            <span className="hidden sm:block font-mono text-xs text-[var(--o-muted)] text-right">{fmtHours(u.usage.seconds)}</span>
            <span className="hidden sm:block font-mono text-xs text-[var(--o-muted)] text-right">{money(u.usage.cost)}</span>
            <span className="hidden sm:block font-mono text-xs text-[var(--o-muted3)] text-right">{fmtDate(u.usage.last_at)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
